import React from 'react';
import { BarChart3, TrendingUp, TrendingDown } from 'lucide-react';

const BarChart = ({ 
  data, 
  title, 
  subtitle, 
  height = 300, 
  showTrend = true,
  showValues = true,
  barColor = 'bg-blue-500',
  hoverColor = 'bg-blue-600',
  gridLines = true,
  animate = true
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center text-gray-500">
          <BarChart3 className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p>No data available</p>
        </div>
      </div>
    );
  }


  // Find the maximum value for scaling
  const maxValue = Math.max(...data.map(item => item.value));
  
  // Calculate percentage for each bar
  const getBarHeight = (value) => {
    return (value / maxValue) * 100;
  };

  // Calculate trend (positive/negative)
  const calculateTrend = () => {
    if (data.length < 2) return { direction: 'neutral', percentage: 0 };
    
    const firstValue = data[0].value;
    const lastValue = data[data.length - 1].value;
    const change = ((lastValue - firstValue) / firstValue) * 100;
    
    return {
      direction: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral',
      percentage: Math.abs(change)
    };
  };

  const trend = calculateTrend();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {subtitle && (
            <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
          )}
        </div>
        
        {showTrend && (
          <div className="flex items-center space-x-2">
            {trend.direction === 'up' ? (
              <TrendingUp className="h-4 w-4 text-green-500" />
            ) : trend.direction === 'down' ? (
              <TrendingDown className="h-4 w-4 text-red-500" />
            ) : null}
            <span className={`text-sm font-medium ${
              trend.direction === 'up' ? 'text-green-600' : 
              trend.direction === 'down' ? 'text-red-600' : 
              'text-gray-600'
            }`}>
              {trend.direction === 'up' ? '+' : trend.direction === 'down' ? '-' : ''}
              {/* {trend.percentage.toFixed(1)}% */}
            </span>
          </div>
        )}
      </div>

      {/* Chart Container */}
      <div className="relative" style={{ height: `${height}px` }}>
        {/* Grid Lines */}
        {gridLines && (
          <div className="absolute inset-0 flex flex-col justify-between">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="border-t border-gray-100"
                style={{ top: `${(i / 4) * 100}%` }}
              />
            ))}
          </div>
        )}

        {/* Y-axis Labels */}
        <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-400">
          {[...Array(5)].map((_, i) => (
            <span key={i} className="transform -translate-y-1/2">
              {Math.round(maxValue * (1 - i / 4)).toLocaleString()}
            </span>
          ))}
        </div>

        {/* Bars */}
        <div className="ml-12 h-full flex items-end space-x-2">

          {data.map((item, index) => (
            <div key={index} className="flex-1 flex flex-col items-center h-full">
              {/* Bar */}
              <div className="flex-1 w-full ">
                <div className={'h-full flex flex-col justify-end'}>
                  <div
                      className={`${barColor} ${hoverColor} group relative transition-all duration-300 rounded-t-sm h-full${
                          animate ? 'animate-pulse' : ''
                      }`}
                      style={{
                        height: `${getBarHeight(item.value)}%`,
                        minHeight: '4px'
                        // height: `${height}px`,
                        // minHeight: `${getBarHeight(item.value)}%`
                      }}
                  >
                    {/* Tooltip on hover */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                      {item.label}: {item.value.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* X-axis Label */}
              <div className="mt-2 text-xs text-gray-600 text-center">
                {item.label}
              </div>
              
              {/* Value Display */}
              {showValues && (
                <div className="mt-1 text-xs font-medium text-gray-900">
                  {item.value.toLocaleString()}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-center space-x-4 text-sm text-gray-600">
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 ${barColor} rounded`}></div>
          <span>Data</span>
        </div>
        {trend.direction !== 'neutral' && (
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded ${
              trend.direction === 'up' ? 'bg-green-500' : 'bg-red-500'
            }`}></div>
            <span>{trend.direction === 'up' ? 'Increasing' : 'Decreasing'}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default BarChart;
